import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, ChevronDown, ChevronRight, Flag, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export default function NoisePatternScanner({ onReReviewFlagged }) {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedCluster, setExpandedCluster] = useState(null);
  const [flagging, setFlagging] = useState(null);

  const handleScan = async () => {
    setScanning(true);
    try {
      const response = await base44.functions.invoke('councilNoisePatternScan', {});
      const data = response.data || response;
      setResult(data);
    } catch (err) {
      console.error('Noise pattern scan failed:', err);
    } finally {
      setScanning(false);
    }
  };

  const handleFlagForReReview = async (issueId, reason) => {
    setFlagging(issueId);
    try {
      await base44.entities.Issue.update(issueId, {
        re_review_flagged: true,
        re_review_reason: reason
      });
      onReReviewFlagged?.(issueId);
    } catch (err) {
      console.error('Flag for re-review failed:', err);
    } finally {
      setFlagging(null);
    }
  };

  const confidenceColors = {
    high: 'bg-red-500/20 text-red-300',
    medium: 'bg-amber-500/20 text-amber-300',
    low: 'bg-slate-500/20 text-slate-300'
  };

  const actionColors = {
    reclassify_as_signal: 'bg-red-500/20 text-red-300',
    monitor_closely: 'bg-amber-500/20 text-amber-300',
    investigate_further: 'bg-cyan-500/20 text-cyan-300'
  };

  const actionLabels = {
    reclassify_as_signal: 'Reclassify as Signal',
    monitor_closely: 'Monitor Closely',
    investigate_further: 'Investigate Further'
  };

  if (scanning) {
    return (
      <Card className="glass-panel border-purple-500/20">
        <CardContent className="py-6 text-center">
          <Loader2 className="w-6 h-6 text-purple-400 animate-spin mx-auto mb-2" />
          <p className="text-sm text-purple-300">Scanning noise archive for hidden patterns...</p>
        </CardContent>
      </Card>
    );
  }

  if (!result) {
    return (
      <Card className="glass-panel border-purple-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-purple-400 flex items-center gap-2">
            <Search className="w-4 h-4" /> Noise Archive Pattern Scanner
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-400 mb-3">
            AI scans dismissed noise items for hidden clusters that may indicate a missed signal.
          </p>
          <Button
            onClick={handleScan}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white h-9 text-sm"
          >
            <Search className="w-4 h-4 mr-2" /> Scan Noise Archive
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-panel border-purple-500/20">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-purple-400 flex items-center gap-2">
            <Search className="w-4 h-4" /> Noise Pattern Scan Results
          </CardTitle>
          <div className="flex items-center gap-2">
            {result.patterns_found > 0 ? (
              <Badge className="bg-amber-500/20 text-amber-300">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {result.patterns_found} pattern{result.patterns_found !== 1 ? 's' : ''} found
              </Badge>
            ) : (
              <Badge className="bg-emerald-500/20 text-emerald-300">
                <ShieldCheck className="w-3 h-3 mr-1" /> No patterns
              </Badge>
            )}
            <Button
              size="sm" variant="ghost"
              onClick={handleScan}
              className="h-7 text-xs text-purple-400 hover:text-purple-300"
            >
              Re-scan
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {result.summary && (
          <p className="text-xs text-slate-300">{result.summary}</p>
        )}

        {(result.clusters || []).map((cluster, idx) => (
          <Collapsible
            key={idx}
            open={expandedCluster === idx}
            onOpenChange={() => setExpandedCluster(expandedCluster === idx ? null : idx)}
          >
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/40 border border-slate-700 hover:border-purple-500/30 transition-colors cursor-pointer">
                {expandedCluster === idx
                  ? <ChevronDown className="w-4 h-4 text-purple-400" />
                  : <ChevronRight className="w-4 h-4 text-purple-400" />
                }
                <span className="text-sm text-slate-200 flex-1 text-left">{cluster.cluster_name}</span>
                <Badge className={confidenceColors[cluster.confidence] || confidenceColors.low}>
                  {cluster.confidence}
                </Badge>
                <Badge className={actionColors[cluster.recommended_action] || actionColors.investigate_further}>
                  {actionLabels[cluster.recommended_action] || cluster.recommended_action}
                </Badge>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 ml-6 space-y-2 p-3 rounded-lg bg-slate-800/20 border border-slate-700/50">
                <p className="text-xs text-slate-300">{cluster.reasoning}</p>
                <div className="flex flex-wrap gap-1">
                  <span className="text-[10px] text-slate-500">Issues:</span>
                  {(cluster.issue_ids || []).map(id => (
                    <Badge key={id} className="bg-slate-700 text-slate-300 text-[10px]">#{id}</Badge>
                  ))}
                </div>
                {cluster.recommended_action === 'reclassify_as_signal' && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {(cluster.issue_ids || []).map(id => (
                      <Button
                        key={id}
                        size="sm"
                        variant="outline"
                        disabled={flagging === id}
                        onClick={() => handleFlagForReReview(id, `Pattern cluster: ${cluster.cluster_name}`)}
                        className="h-6 text-[10px] border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        {flagging === id
                          ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          : <Flag className="w-3 h-3 mr-1" />
                        }
                        Flag #{id} for re-review
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </CardContent>
    </Card>
  );
}